/**
 * F-38 connector layer: the curved lines between elements, plus the line being
 * dragged out of a port.
 *
 * It lives INSIDE the shared camera transform (docs/04 §5) like the table and
 * the elements, and paints BEFORE them in DOM order — a line runs under the
 * elements it joins, which is both how every canvas tool draws them and what
 * makes clicking an element over a line unambiguous.
 *
 * Geometry is derived, never stored (see ./geometry): every pass resolves both
 * endpoint elements to a board rect and re-computes the curve. That is what
 * makes "the line follows the item" true without any event wiring — and the
 * resolution order is the same one comment pins use (docs/04 §3), so a line
 * rides a drag live on BOTH sides of the wire:
 *
 *   our own live drag rect  →  a peer's awareness-streamed rect (C9)  →  doc
 *
 * Hit-testing is an invisible wide stroke under the visible one rather than
 * curve math: `pointer-events: stroke` gives the browser's own path hit-testing
 * a comfortable target at any zoom, and it stays exact when the curve changes.
 */
import type { ConnectorEndpoint, ConnectorInfo } from "@kola/shared";
import { screenToBoard } from "@kola/shared";
import { useCallback, useEffect, useMemo, useRef, type RefObject } from "react";
import { useStore } from "zustand";

import type { CanvasStore } from "../../core/canvas.js";
import { rectIntersectsCull } from "../../core/row-window.js";
import type { BoardSession } from "../../core/session.js";
import { useCullRect } from "../../core/useCullRect.js";
import type { BoardRect } from "../../registries/toolbar-actions/index.js";
import type { GridLayoutSnapshot } from "../board/GridTable.js";
import { resolveElementRect } from "../elements/contained.js";
import type { ElementsUiStore } from "../elements/store.js";
import {
  canConnectTo,
  connectorBounds,
  connectorPath,
  nearestPort,
  portCandidates,
  portPoint,
  type Point,
} from "./geometry.js";
import { PORT_SNAP_SCREEN_PX, type ConnectorUiStore } from "./store.js";

/** Visible stroke width (screen px — counter-scaled so zoom doesn't fatten it). */
const STROKE_SCREEN_PX = 2;
/** Invisible click target around the curve (screen px). */
const HIT_STROKE_SCREEN_PX = 14;

export interface ConnectorsLayerProps {
  session: BoardSession;
  canvas: CanvasStore;
  /** The canvas element, for screen→board conversion of window pointer events. */
  containerRef: RefObject<HTMLElement | null>;
  /** F-36: the grid's layout pass — cell-anchored endpoints resolve through it. */
  layout: GridLayoutSnapshot | null;
  elementsUi: ElementsUiStore;
  ui: ConnectorUiStore;
  /** Selecting a line clears the element selection (BoardCanvas owns both). */
  onSelectConnector: (connectorId: string | null) => void;
}

export function ConnectorsLayer({
  session,
  canvas,
  containerRef,
  layout,
  elementsUi,
  ui,
  onSelectConnector,
}: ConnectorsLayerProps) {
  const zoom = useStore(canvas, (s) => s.camera.zoom);
  const cull = useCullRect(canvas);
  const elementsVersion = useStore(session.store, (s) => s.elementsVersion);
  const connectorsVersion = useStore(session.store, (s) => s.connectorsVersion);
  const synced = useStore(session.store, (s) => s.synced);
  const frozen = useStore(session.store, (s) => s.frozen);
  const peers = useStore(session.store, (s) => s.peers);
  const live = useStore(elementsUi, (s) => s.live);
  const draft = useStore(ui, (s) => s.draft);
  const selectedId = useStore(ui, (s) => s.selectedId);

  const elements = useMemo(() => {
    void elementsVersion;
    return session.board.listElements();
  }, [session, elementsVersion]);

  const connectors = useMemo(() => {
    void connectorsVersion;
    return session.board.listConnectors();
  }, [session, connectorsVersion]);

  /**
   * Every element's rect for THIS pass, under the docs/04 §3 precedence: our
   * own in-flight drag first (awareness never echoes our drag back to us), then
   * a peer's streamed preview (C9), then the committed doc geometry.
   */
  const rects = useMemo(() => {
    const grabs = new Map<string, BoardRect>();
    for (const peer of peers) {
      const grab = peer.grabbedElement;
      if (grab?.rect !== undefined) grabs.set(grab.elementId, grab.rect);
    }
    const map = new Map<string, BoardRect>();
    for (const element of elements) {
      map.set(
        element.id,
        live.get(element.id) ?? grabs.get(element.id) ?? resolveElementRect(element, layout),
      );
    }
    return map;
  }, [elements, layout, live, peers]);

  /** Committed connectors whose BOTH ends resolve, with their curve endpoints. */
  const drawable = useMemo(() => {
    const out: Array<{ connector: ConnectorInfo; from: Point; to: Point }> = [];
    for (const connector of connectors) {
      const fromRect = rects.get(connector.from.elementId);
      const toRect = rects.get(connector.to.elementId);
      if (fromRect === undefined || toRect === undefined) continue;
      out.push({
        connector,
        from: portPoint(fromRect, connector.from.side),
        to: portPoint(toRect, connector.to.side),
      });
    }
    return out;
  }, [connectors, rects]);

  // C4 sweep (docs/07): a line whose element is gone — drawn concurrently with
  // the delete, or synced after it — is deleted by whichever editing client
  // observes it first. Gated on `synced` (an un-synced doc proves nothing about
  // existence) and on the F-45 freeze, exactly like the contained-element
  // degradation next door in ElementsLayer. Idempotent across every racer.
  useEffect(() => {
    if (!synced || frozen) return;
    const missing = connectors.some(
      (c) => !session.board.hasElement(c.from.elementId) || !session.board.hasElement(c.to.elementId),
    );
    if (missing) session.sweepConnectors();
  }, [session, connectors, synced, frozen]);

  /** Window pointer position → board units (the draft drag leaves the layer). */
  const pointerToBoard = useCallback(
    (event: PointerEvent): Point => {
      const container = containerRef.current;
      const bounds = container?.getBoundingClientRect();
      return screenToBoard(canvas.getState().camera, {
        x: event.clientX - (bounds?.left ?? 0),
        y: event.clientY - (bounds?.top ?? 0),
      });
    },
    [canvas, containerRef],
  );

  /* ---------------- the draft drag ----------------------------------- */

  // The draft STARTS on a port inside ElementsLayer (that is where the ports
  // are drawn) and is driven to completion here, on window listeners: a drag
  // may travel anywhere over the canvas — including off every element — and
  // must still track and still be cancellable.
  // The snap targets change on every frame of the drag (they ride the same
  // rects everything else does), so they are read through a ref: keeping them
  // in the effect's dependencies would tear down and re-register the window
  // listeners on every pointer frame.
  const snapInputsRef = useRef({ elements, rects, zoom });
  snapInputsRef.current = { elements, rects, zoom };

  const draftFrom = draft?.from;
  const draftPointerId = draft?.pointerId;
  useEffect(() => {
    if (draftFrom === undefined || draftPointerId === undefined) return;
    const state = () => ui.getState();

    const onMove = (event: PointerEvent) => {
      if (event.pointerId !== draftPointerId) return;
      const pointer = pointerToBoard(event);
      const inputs = snapInputsRef.current;
      // Candidates in FRONT-to-back order so a tie snaps to the frontmost
      // element — `elements` is (z, id) paint order, i.e. back to front.
      const candidates = portCandidates(
        [...inputs.elements]
          .reverse()
          .filter((element) => canConnectTo(element.type))
          .map((element) => ({ id: element.id, rect: inputs.rects.get(element.id) }))
          .filter((entry): entry is { id: string; rect: BoardRect } => entry.rect !== undefined),
      );
      const snap = nearestPort(candidates, pointer, PORT_SNAP_SCREEN_PX / inputs.zoom);
      // A line to the port it started from is not a connection; every OTHER
      // port — including another side of the same element — is fair game.
      state().updateDraft(
        pointer,
        snap !== null && snap.elementId === draftFrom.elementId && snap.side === draftFrom.side
          ? null
          : snap,
      );
    };

    const onUp = (event: PointerEvent) => {
      if (event.pointerId !== draftPointerId) return;
      const snap = state().draft?.snap ?? null;
      state().endDraft();
      if (snap === null) return; // released over open canvas — nothing drawn
      const to: ConnectorEndpoint = { elementId: snap.elementId, side: snap.side };
      const created = session.createConnector(draftFrom, to);
      if (created !== null) onSelectConnector(created);
    };

    const onCancel = (event: PointerEvent) => {
      if (event.pointerId === draftPointerId) state().endDraft();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") state().endDraft();
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [draftFrom, draftPointerId, ui, session, pointerToBoard, onSelectConnector]);

  /* ---------------- render ------------------------------------------- */

  const inv = 1 / zoom;
  // The SVG covers the culled board region and its content is translated into
  // that box, rather than an unsized element with overflow — an overflowing
  // SVG paints outside its viewport but is not reliably hit-testable there, and
  // these paths ARE the click target. Anything clipped at this boundary is a
  // full viewport off-screen.
  const width = Math.max(1, cull.right - cull.left);
  const height = Math.max(1, cull.bottom - cull.top);

  const draftGeometry = useMemo(() => {
    if (draft === null) return null;
    const fromRect = rects.get(draft.from.elementId);
    if (fromRect === undefined) return null; // the source went away mid-drag
    const from = portPoint(fromRect, draft.from.side);
    const snapRect = draft.snap === null ? undefined : rects.get(draft.snap.elementId);
    // Snapped: preview the REAL curve it would create (both ports, both
    // normals) — not the pointer-following line — so what the user sees before
    // release is what release commits.
    return draft.snap !== null && snapRect !== undefined
      ? { d: connectorPath(from, draft.from.side, draft.snap.point, draft.snap.side), snapped: true }
      : { d: connectorPath(from, draft.from.side, draft.pointer, oppositeOf(draft.from.side)), snapped: false };
  }, [draft, rects]);

  const visible = drawable.filter(({ connector, from, to }) =>
    rectIntersectsCull(connectorBounds(from, connector.from.side, to, connector.to.side), cull),
  );

  return (
    <svg
      className="connectors-layer"
      style={{ left: cull.left, top: cull.top, width, height }}
      width={width}
      height={height}
      aria-hidden="true"
    >
      <g transform={`translate(${-cull.left}, ${-cull.top})`}>
        {visible.map(({ connector, from, to }) => {
          const d = connectorPath(from, connector.from.side, to, connector.to.side);
          const selected = connector.id === selectedId;
          return (
            <g key={connector.id}>
              <path
                className="connector-hit"
                d={d}
                strokeWidth={HIT_STROKE_SCREEN_PX * inv}
                onPointerDown={(event) => {
                  event.stopPropagation();
                  onSelectConnector(connector.id);
                }}
              />
              <path
                className={`connector-line${selected ? " selected" : ""}`}
                d={d}
                strokeWidth={(selected ? STROKE_SCREEN_PX + 1 : STROKE_SCREEN_PX) * inv}
              />
            </g>
          );
        })}
        {draftGeometry !== null && (
          <path
            className={`connector-draft${draftGeometry.snapped ? " snapped" : ""}`}
            d={draftGeometry.d}
            strokeWidth={STROKE_SCREEN_PX * inv}
            strokeDasharray={draftGeometry.snapped ? undefined : `${6 * inv} ${5 * inv}`}
          />
        )}
      </g>
    </svg>
  );
}

/** The side a free end faces — used only to bow the un-snapped draft sensibly. */
function oppositeOf(side: ConnectorEndpoint["side"]): ConnectorEndpoint["side"] {
  switch (side) {
    case "top":
      return "bottom";
    case "bottom":
      return "top";
    case "left":
      return "right";
    case "right":
      return "left";
  }
}
